import { useLang } from 'hooks/lang';
import { useMemo } from 'react';

import { LoadResults } from 'entities';

import { LabeledSelect } from 'components/Blocks';
import { WrenchIcon } from 'components/Icons';

import { getID } from 'utils/id';
import { listGet, sortByName, sortByProperty } from 'utils/array';

import { CreateProjectDispatchAction, ProjectCreationProps } from '../types';

interface ServicesProps {
  dispatch: CreateProjectDispatchAction;
  state: ProjectCreationProps;
  data: LoadResults['teams.allServices'];
  loading: boolean;
}

export function ServicesSelect({ dispatch, state, loading, data }: ServicesProps): JSX.Element {
  const { t } = useLang();

  const handleSelectChange = (servicesSelected: string[]): void => {
    dispatch({
      services: ({ services, countries, currency }) =>
        sortByName(
          servicesSelected
            .map((serviceId) => {
              const existingServices = services.filter(({ service }) => service === serviceId);

              if (existingServices.length) return existingServices;

              const { name, costVariables, costCalculation } = listGet(data, serviceId)!;

              const baseCosts = costVariables.map((cost) => ({
                ...cost,
                value: '',
                currency: cost.type === 'CURRENCY' ? currency : undefined,
              }));

              return {
                service: serviceId,
                id: getID(),
                name,
                baseCosts: costVariables,
                costCalculation,
                costs: Object.fromEntries(countries.map((country) => [country, baseCosts])),
              };
            })
            .flat(),
        ),
    });
  };

  const serviceOptions = useMemo(() => {
    const services = data.map(({ name, team, ...rest }) => ({
      ...rest,
      label: `${team.name} - ${name}`,
      teamName: team.name,
      serviceName: name,
    }));

    return sortByProperty(services, 'label');
  }, [data]);

  const selectedServices = state.services.map(({ service }) => service);

  return (
    <LabeledSelect
      options={serviceOptions}
      value={selectedServices}
      onChangeDirect={handleSelectChange}
      label={t('quanti-quali-services') || ''}
      isMulti
      loading={loading}
      labelIcon={WrenchIcon}
      formatLabel={({ teamName, serviceName }) => (
        <span>
          <strong>{teamName}</strong> - {serviceName}
        </span>
      )}
    />
  );
}
